import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  InputGroup,
  Tab,
  Tabs,
} from "react-bootstrap";
import validate from "validate.js";
import { isEmpty, find } from "lodash";
import useRouter from "utils/useRouter";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CK_CONFIGS } from "configs/ckEditor";
import {
  addRoles,
  updateRoles,
  getRolesById,
  hideRolesValidationError,
} from "pages/AccessManagement/actions";

const schema = {
  name: {
    presence: { allowEmpty: false, message: "^Role name is required" },
  },
};

const AddUpdateModel = ({ show, onHide, ...rest }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const rolesState = useSelector((state) => state.rolesState);
  const session = useSelector((state) => state.session);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      object_viewed_id: session.current_page_permissions.object_id,
    },
    touched: {
      object_viewed_id: true,
    },
    errors: {},
  });

  useEffect(() => {
    if (rolesState.showUpdateForm) {
      let record = rolesState.rolesRecord;
      setFormState((formState) => ({
        ...formState,
        values: {
          ...formState.values,
          name: record.name,
          id: record.id,
          description: record.description,
        },
        touched: {
          ...formState.touched,
          name: true,
          id: true,
          description: true,
        },
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesState.rolesRecord]);

  useEffect(() => {
    if (!isEmpty(rolesState.validation_error)) {
      const errors = rolesState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [rolesState.validation_error]);

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState((formState) => ({
      ...formState,
      isValid: !errors,
      errors: errors || {},
    }));
  }, [formState.values]);

  const onModelClose = () => {
    onHide();
    resetFormState();
  };

  const resetFormState = () => {
    setFormState({
      isValid: false,
      values: {
        object_viewed_id: session.current_page_permissions.object_id,
      },
      touched: {
        object_viewed_id: true,
      },
      errors: {},
    });
  };

  const handleChange = (event) => {
    event.persist();

    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));

    dispatch(hideRolesValidationError(event.target.name));
  };

  const setDescription = (description) => {
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        description: description,
      },
      touched: {
        ...formState.touched,
        description: true,
      },
    }));
    dispatch(hideRolesValidationError("description"));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (rolesState.showUpdateForm) {
      dispatch(updateRoles(formState.values));
    } else {
      dispatch(addRoles(formState.values));
    }
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  const showViewForm = (recordId) => {
    dispatch(getRolesById(recordId, "view"));
  };

  return (
    <>
      <Modal
        show={show}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="p-4"
      >
        <Modal.Header className="d-block">
          <Modal.Title id="contained-modal-title-vcenter" className="">
            <h3 className="font-20 font-weight-s-bold heading-color">Roles</h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="align-items-center">
            <Col lg={12}>
              <div className="roles-modal">
                <Form.Group>
                  <Form.Label className="font-13 font-weight-400 page-heading">
                    Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter name"
                    name="name"
                    onChange={handleChange}
                    value={formState.values.name || ""}
                    className={`font-13 font-weight-normal h-40 border-radius-51 basic-input ${
                      hasError("name") ? "is-invalid" : ""
                    }`}
                  ></Form.Control>
                  {hasError("name") && (
                    <Form.Control.Feedback type="invalid">
                      {formState.errors.name[0]}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
            </Col>
          </Row>
          <div className="mt-3">
            <CKEditor
              editor={ClassicEditor}
              config={CK_CONFIGS(localStorage.getItem("token"))}
              data={formState.values.description || ""}
              onChange={(event, editor) => {
                const data = editor.getData();
                setDescription(data);
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex align-items-center">
          <div className="d-flex flex-column flex-md-row gap-3 w-100">
            <button
              onClick={handleSubmit}
              disabled={!formState.isValid}
              variant="none"
              className="n-u-btn w-100"
            >
              Save
            </button>
            {rolesState.showUpdateForm ? (
              <button
                className="n-u-btn w-100"
                variant="none"
                onClick={() => showViewForm(formState.values.id)}
              >
                View Details
              </button>
            ) : (
              ""
            )}
            <button
              className="c-b-btn w-100"
              variant="none"
              onClick={() => onModelClose()}
            >
              Cancel
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddUpdateModel;
